<template>
  <CCard class="setting-edit">
    <CCardHeader>
      <div class="setting-edit__head">
        <div>
          <strong>{{ title }}</strong>
          <p v-if="summary">{{ summary }}</p>
        </div>
        <CButton
          type="button"
          color="secondary"
          variant="outline"
          size="sm"
          @click="back"
        >自社設定へ戻る</CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>

      <div v-if="loading" class="setting-edit__loading">読み込んでいます…</div>

      <!--
        **読めていないあいだは中身を出さない。**
        空のフォームを出すと、その空で保存できてしまう。
      -->
      <template v-else-if="loaded">
        <slot :form="form"/>
      </template>

      <!--
        **決定は1つだけ。場所もここだけ。**
        画面ごとに置き場所を決めていたころは、右・左・中とばらばらで、
        どこを押せば決まるのか画面ごとに探すことになっていた。
      -->
      <div v-if="loaded && !loading" class="setting-edit__actions">
        <CButton
          type="button"
          color="secondary"
          variant="outline"
          :disabled="saving"
          @click="back"
        >やめる</CButton>
        <CButton
          type="button"
          color="primary"
          :disabled="saving"
          @click="save"
        >{{ saving ? '保存しています…' : '保存する' }}</CButton>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import api from '@/services/companySettings'
import { READ_ONLY_KEYS, isOvertimeAgreementKey } from './settingSections'
import { safeErrorDigest } from '@/utils/safeResponseMessage'

/**
 * 自社設定の「1項目だけ直す」画面の共通の枠。
 *
 * <h2>なぜ枠を1つにするのか</h2>
 *
 * 決定ボタンの<b>置き場所を画面に決めさせない。</b>
 * 決めさせていたころは、同じ自社設定の中で右・左・中とばらばらで、
 * 押す場所を毎回探すことになっていた。ここが持てば必ず揃う。
 *
 * <h2>全部を送る</h2>
 *
 * サーバの <code>/setting/update</code> は全項目を受け取って
 * 会社の行を作り直す。<b>担当ぶんだけ送ると、送らなかった項目が消える。</b>
 * だから読み込んだ全項目を保ったまま、担当だけ上書きして送る。
 *
 * 36協定と、表示のためだけに返る項目は落とす（送ると上書きされる）。
 */
export default {
  name: 'SettingEditLayout',

  props: {
    title: { type: String, required: true },
    summary: { type: String, default: '' },
    /** この画面が編集してよい項目。ここに無いものは触らせない。 */
    fields: { type: Array, required: true },
    /** 保存の前に呼ぶ。文字列を返したらその文言で断る。 */
    validate: { type: Function, default: null }
  },

  data () {
    return {
      /** サーバから読んだ全項目。**送るときの土台。** */
      received: {},
      /** 画面で編集している値。担当ぶんだけ。 */
      form: {},
      loading: true,
      loaded: false,
      saving: false,
      errorMessage: ''
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.errorMessage = ''
      try {
        const received = await api.get()
        this.received = received && typeof received === 'object' ? received : {}
        const form = {}
        this.fields.forEach(key => {
          form[key] = this.received[key] === undefined ? '' : this.received[key]
        })
        this.form = form
        this.loaded = true
      } catch (error) {
        this.errorMessage = '自社設定を読み込めませんでした。'
          + ' 時間をおいて開き直してください。'
        this.$emit('load-error', safeErrorDigest(error))
      } finally {
        this.loading = false
      }
    },

    async save () {
      if (this.saving) return
      if (typeof this.validate === 'function') {
        const refusal = this.validate(this.form)
        if (refusal) { this.errorMessage = refusal; return }
      }

      this.saving = true
      this.errorMessage = ''
      try {
        // **読んだ全項目を土台にする。**担当ぶんだけ上から書く。
        const payload = Object.assign({}, this.received, this.form)

        // 表示のためだけに返るものは送らない。
        READ_ONLY_KEYS.forEach(key => { delete payload[key] })
        // 36協定は専用の口がある。ここから送ると、いま効いている協定が
        // 画面の値で丸ごと上書きされる。
        Object.keys(payload).forEach(key => {
          if (isOvertimeAgreementKey(key)) { delete payload[key] }
        })

        await api.update(payload)
        this.$emit('saved')
        this.$router.push({ name: '設定' })
      } catch (error) {
        this.errorMessage = this.refusalMessage(error)
      } finally {
        this.saving = false
      }
    },

    /**
     * 断られた理由を、そのまま出す。
     *
     * <b>「システムエラー」で潰さない。</b>サーバは「就業時間が
     * 前の日へ回り込む」のような、直せる理由を返してくる。
     */
    refusalMessage (error) {
      const body = error && error.response && error.response.data
      const message = body && typeof body.message === 'string' ? body.message.trim() : ''
      return message || '保存できませんでした。入力内容を確認してください。'
    },

    back () {
      this.$router.push({ name: '設定' })
    }
  }
}
</script>

<style scoped>
.setting-edit__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.setting-edit__head p {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: #5c6873;
}

.setting-edit__loading {
  padding: 2rem 0;
  text-align: center;
  color: #5c6873;
}

/*
  **決定はいつもここ。**右下に、やめる／保存するの順で並べる。
  画面が変わっても位置が変わらないことが、この枠のいちばんの仕事。
*/
.setting-edit__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #d8dbe0;
}

@media (max-width: 575px) {
  .setting-edit__head {
    flex-direction: column;
  }
  /* 画面が狭いときは、押しやすいよう横いっぱいにする。 */
  .setting-edit__actions {
    flex-direction: column-reverse;
  }
  .setting-edit__actions > * {
    width: 100%;
  }
}
</style>
